import { CalendarViewOptions, ID } from '@griply/types';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useSortedSmartLists } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import React, { Suspense, useMemo, useRef } from 'react';
import { XCircle } from 'shared/assets/icons/svgs';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageLoader } from 'shared/components/ui/page-loader';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useUser } from 'shared/hooks/use-user';
import { targetIsInput } from 'shared/utils/target-is-input';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { useSelectedCalendarView } from '../../hooks/use-selected-calendar-view';
import { useSidePanelListConfig } from '../../hooks/use-side-panel-list-config';
import {
  DefaultSideOptions,
  DefaultViewMode,
  ExtraSideOptions,
  ExtraViewMode,
  ViewMode,
} from '../../types/side-panel';
import * as Styled from './side-panel.style';
import {
  SidePanelViewSelector,
  SidePanelViewSelectorHandle,
} from './side-panel-view-selector';

export type SidePanelProps = {
  onClose: () => void;
};

export const SidePanel: React.FC<SidePanelProps> = ({ onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedView = useSelectedCalendarView();
  const viewSelectorRef = useRef<SidePanelViewSelectorHandle>(null);

  const user = useUser();
  const { isMac } = useElectron();

  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const goals = useSortedGoals();
  const tags = useSortedTags();
  const smartLists = useSortedSmartLists({ excludeFrozen: true });

  const updateUserSettings = useUpdateUserSettings();

  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    const list = user?.settings?.upcoming?.calendarList;

    if (list === DefaultSideOptions.Inbox) {
      return { type: DefaultSideOptions.Inbox };
    }
    if (list === DefaultSideOptions.AllTasks) {
      return { type: DefaultSideOptions.AllTasks };
    }
    if (list === DefaultSideOptions.Habits) {
      return { type: DefaultSideOptions.Habits };
    }

    const smartList = smartLists.find(({ id }) => id === list);
    if (smartList) {
      return {
        type: ExtraSideOptions.SmartList,
        id: smartList.id,
        name: smartList.name,
      };
    }

    const tag = tags.find(({ id }) => id === list);
    if (tag) {
      return {
        type: ExtraSideOptions.Tag,
        id: tag.id,
        name: tag.name,
      };
    }

    return { type: DefaultSideOptions.AllTasks };
  });

  const viewModeId = (viewMode as ExtraViewMode).id;

  const selectedSmartList = useMemo(
    () => smartLists.find(({ id }) => id === viewModeId),
    [smartLists, viewModeId],
  );

  const selectedTag = useMemo(
    () => tags.find(({ id }) => id === viewModeId),
    [tags, viewModeId],
  );

  const listConfig = useSidePanelListConfig(
    viewMode,
    selectedTag,
    selectedSmartList,
  );

  const currentViewValue = useMemo(() => {
    return [
      DefaultSideOptions.Inbox,
      DefaultSideOptions.AllTasks,
      DefaultSideOptions.Habits,
    ].includes((viewMode as DefaultViewMode).type)
      ? viewMode.type
      : (viewMode as ExtraViewMode).id;
  }, [viewMode]);

  const handleViewChange = (value: DefaultSideOptions | ID) => {
    if (!value) {
      return;
    }

    updateUserSettings({ upcoming: { calendarList: value } });

    if (
      [
        DefaultSideOptions.Inbox,
        DefaultSideOptions.AllTasks,
        DefaultSideOptions.Habits,
      ].includes(value as DefaultSideOptions)
    ) {
      setViewMode({
        type: value as DefaultSideOptions,
      });
      return;
    }

    const smartList = smartLists.find(({ id }) => id === value);
    if (smartList) {
      setViewMode({
        type: ExtraSideOptions.SmartList,
        id: smartList.id,
        name: smartList.name,
      });
      return;
    }

    const tag = tags.find(({ id }) => id === value);
    if (!tag) {
      return;
    }
    setViewMode({
      type: ExtraSideOptions.Tag,
      id: tag.id,
      name: tag.name,
    });
  };

  useKeysDown(
    [['KeyS']],
    (e) => {
      if (targetIsInput(e.target)) {
        return;
      }

      viewSelectorRef.current?.open();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.Container>
      <Styled.Header $isElectronMac={isMac}>
        <SidePanelViewSelector
          ref={viewSelectorRef}
          tags={tags}
          smartLists={smartLists}
          value={currentViewValue}
          onChange={handleViewChange}
        />

        <Styled.HeaderInteractions>
          <FilterButton
            filterDocument={listConfig.filterDocument}
            defaultFilterDocument={listConfig.defaultFilterDocument}
            supportedFilters={listConfig.supportedFilters}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={listConfig.updateFilterDocument}
            supportedGroupByModes={listConfig.supportedGroupByModes}
            supportedSortingModes={listConfig.supportedSortingModes}
            onReset={listConfig.onReset}
            onUpdateSmartList={
              viewMode.type === ExtraSideOptions.SmartList
                ? listConfig.onSaveSmartList
                : undefined
            }
            renderAsIconButton
            preventDrag
          />
          <IconButton
            icon={XCircle}
            onClick={onClose}
            color={Colors.Default}
            preventDrag
          />
        </Styled.HeaderInteractions>
      </Styled.Header>

      <Styled.Content ref={scrollRef}>
        <Suspense fallback={<PageLoader />}>
          <ProvidedTaskFilterList
            // Force re-render when switching views to ensure clean state
            key={
              viewMode.type === ExtraSideOptions.Tag
                ? `tag-${viewMode.id}`
                : viewMode.type
            }
            filterDocument={listConfig.filterDocument}
            defaultFilterDocument={listConfig.defaultFilterDocument}
            onReset={listConfig.onReset}
            customOrder={listConfig.customOrder}
            onReorder={listConfig.updateCustomOrder}
            onSectionCollapse={listConfig.onSectionCollapse}
            groupSettingOverrides={listConfig.groupSettingOverrides}
            scrollElement={scrollRef}
            includeDuration={selectedView !== CalendarViewOptions.Month}
            hideCompletedGoalsWithoutScheduledTasks={
              viewMode.type === DefaultSideOptions.AllTasks
            }
            denseFormat
          />
        </Suspense>
      </Styled.Content>
    </Styled.Container>
  );
};
